import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
	Menu,
	MenuItem,
	Box,
	TextField,
	Typography,
	Checkbox,
	Button,
} from '@mui/material';
import { connect } from 'react-redux';
import { DARK_MODE } from '../../constants';
import { getScaleFactor } from '../../theme/resolutionConfig';

const ColumnMenu = ({
	open,
	handleClose,
	anchorEl,
	columns,
	onColumnChange,
	defaultColumns,
	themeMode,
}) => {
	const [searchQuery, setSearchQuery] = useState('');

	// --- Filter visible columns based on search ---
	const filterOptions = (cols, query) => {
		if (!query) return cols;
		return cols.filter((col) =>
			(col.title || col.field || '').toLowerCase().includes(query.toLowerCase()),
		);
	};

	const filteredColumns = filterOptions(columns, searchQuery);

	// --- Handle toggle of single column ---
	const handleToggleColumn = (field) => {
		const updated = columns.map((col) =>
			col.field === field ? { ...col, hidden: !col.hidden } : col,
		);
		onColumnChange?.(updated);
	};

	// --- Handle "Select All" ---
	const handleSelectAll = () => {
		const allVisible = columns.every((col) => !col.hidden);
		const updated = columns.map((col) => ({ ...col, hidden: allVisible }));
		onColumnChange?.(updated);
	};

	// --- Handle Reset to Default ---
	const handleResetDefault = () => {
		const updated = defaultColumns.map((defCol) => ({
			...defCol,
			hidden: false,
		}));
		onColumnChange?.(updated);
		setSearchQuery('');
	};

	// --- Scaling setup ---
	const scaleFactor = getScaleFactor(window.innerWidth) * 0.8;
	const baseWidth = 420;
	const baseMaxWidth = 480;
	const baseHeight = 500;
	const basePadding = 24;

	return (
		<Menu
			anchorEl={anchorEl}
			open={open}
			onClose={handleClose}
			PaperProps={{
				sx: {
					minWidth: baseWidth * scaleFactor,
					maxWidth: baseMaxWidth * scaleFactor,
					maxHeight: baseHeight * scaleFactor,
					overflowY: 'auto',
					background:
						themeMode === DARK_MODE
							? 'linear-gradient(115.48deg, #1A2127 31.9%, #151A1F 100%)'
							: '#F2F2F2',
					borderRadius: `${12 * scaleFactor}px`,
					marginTop: `${8 * scaleFactor}px`,
				},
			}}
			MenuListProps={{ sx: { p: 0 } }}
			anchorOrigin={{
				vertical: 'bottom',
				horizontal: 'right',
			}}
			transformOrigin={{
				vertical: 'top',
				horizontal: 'right',
			}}
		>
			<Box p={`${basePadding * scaleFactor}px`} minWidth={380 * scaleFactor}>
				<Typography
					variant="subtitle1"
					sx={{
						mb: 2,
						fontSize: `${1.1 * scaleFactor}rem`,
						fontWeight: 600,
						color: themeMode === DARK_MODE ? '#F8F8F8' : '#111827',
					}}
				>
					Column Options
				</Typography>

				<TextField
					fullWidth
					size="medium"
					placeholder="Search columns..."
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					sx={{
						mb: 2,
						'& .MuiOutlinedInput-root': {
							color: themeMode === DARK_MODE ? '#F8F8F8' : '#111827',
							fontSize: `${1.1 * scaleFactor}rem`,
							fontWeight: 600,
							'& fieldset': {
								borderColor:
									themeMode === DARK_MODE ? 'rgba(229,231,235,0.3)' : '#CBD5E1',
							},
							'&:hover fieldset': {
								borderColor: themeMode === DARK_MODE ? '#94A3B8' : '#475569',
							},
							'&.Mui-focused fieldset': {
								borderColor: themeMode === DARK_MODE ? '#60a5fa' : '#2563EB',
							},
						},
						'& .MuiInputBase-input': {
							color: themeMode === DARK_MODE ? '#F8F8F8' : '#111827',
							padding: `${12 * scaleFactor}px ${14 * scaleFactor}px`,
						},
					}}
				/>

				{/* Select All */}
				<Box mb={2.5}>
					<MenuItem
						onClick={handleSelectAll}
						sx={{
							borderRadius: `${8 * scaleFactor}px`,
							py: `${12 * scaleFactor}px`,
							fontSize: `${1.1 * scaleFactor}rem`,
							fontWeight: 600,
							color: themeMode === DARK_MODE ? '#F8F8F8' : '#111827',
							borderBottom: themeMode === DARK_MODE
								? '1px solid rgba(255, 255, 255, 0.1)'
								: '1px solid rgba(0, 0, 0, 0.1)',
							'&:hover': {
								backgroundColor:
									themeMode === DARK_MODE
										? 'rgba(255,255,255,0.04)'
										: 'rgba(0,0,0,0.04)',
							},
						}}
					>
						<Checkbox
							checked={columns.every((col) => !col.hidden)}
							indeterminate={
								columns.some((col) => col.hidden) &&
								!columns.every((col) => col.hidden)
							}
							sx={{
								color: themeMode === DARK_MODE ? '#374151' : '#9CA3AF',
								'&.Mui-checked': {
									color: themeMode === DARK_MODE ? '#60a5fa' : '#2331afff',
								},
								'& .MuiSvgIcon-root': {
									fontSize: `${1.4 * scaleFactor}rem`,
								},
							}}
						/>
						Select All
					</MenuItem>
				</Box>

				{/* Column toggles */}
				{filteredColumns.map((col) => (
					<MenuItem
						key={col.field}
						onClick={() => handleToggleColumn(col.field)}
						sx={{
							borderRadius: `${8 * scaleFactor}px`,
							py: `${12 * scaleFactor}px`,
							fontSize: `${1.1 * scaleFactor}rem`,
							fontWeight: 600,
							color: themeMode === DARK_MODE ? '#F8F8F8' : '#111827',
							'&:hover': {
								backgroundColor:
									themeMode === DARK_MODE
										? 'rgba(255,255,255,0.04)'
										: 'rgba(0,0,0,0.04)',
							},
						}}
					>
						<Checkbox
							checked={!col.hidden}
							sx={{
								color: themeMode === DARK_MODE ? '#374151' : '#9CA3AF',
								'&.Mui-checked': {
									color: themeMode === DARK_MODE ? '#60a5fa' : '#2331afff',
								},
								'& .MuiSvgIcon-root': {
									fontSize: `${1.4 * scaleFactor}rem`,
								},
							}}
						/>
						{col.title || col.field}
					</MenuItem>
				))}

				{/* Reset button */}
				<Box
					mt={2}
					pt={2}
					borderTop={`${1 * scaleFactor}px solid ${
						themeMode === DARK_MODE
							? 'rgba(255, 255, 255, 0.1)'
							: 'rgba(0, 0, 0, 0.12)'
					}`}
				>
					<Button
						size="medium"
						onClick={handleResetDefault}
						sx={{
							fontSize: `${0.95 * scaleFactor}rem`,
							fontWeight: 500,
							color: themeMode === DARK_MODE ? '#F87171' : '#DC2626',
							'&:hover': {
								backgroundColor:
									themeMode === DARK_MODE
										? 'rgba(248,113,113,0.1)'
										: 'rgba(220,38,38,0.1)',
							},
						}}
					>
						Reset to Default
					</Button>
				</Box>
			</Box>
		</Menu>
	);
};

ColumnMenu.propTypes = {
	open: PropTypes.bool.isRequired,
	handleClose: PropTypes.func.isRequired,
	anchorEl: PropTypes.instanceOf(Element),
	columns: PropTypes.array.isRequired,
	onColumnChange: PropTypes.func.isRequired,
	defaultColumns: PropTypes.array.isRequired,
	themeMode: PropTypes.string,
};

ColumnMenu.defaultProps = {
	open: false,
	handleClose: () => {},
	anchorEl: null,
	columns: [],
	onColumnChange: () => {},
	defaultColumns: [],
	themeMode: 'light',
};

const mapStateToProps = (state) => ({
	themeMode: state.theme.themeMode,
});

export default connect(mapStateToProps)(ColumnMenu);
